import {useApiCall} from '@deezer/react-legacy-api';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from '../useLocation';
import {useRouter} from 'next/router';
import {useToast} from '@tempo/core';
import {useTranslation} from '../useTranslation';
import {config} from '@app/modules/config';
import {useAutolog} from '@app/hooks/auth/useAutolog';
import {AuthStatus} from '@app/hooks/auth/useAutolog/utils';

type GiftCodeError = {
	payload: {
		GIFT_CODE_ERROR: string;
	};
};
type GiftCodeRedeemError =
	| {
			name: 'USER_NOT_LOGGED';
			response: {
				error: {
					USER_NOT_LOGGED?: string;
				};
			};
	  }
	| {
			name: 'GIFT_CODE_ERROR';
			response: {
				error: {
					GIFT_CODE_ERROR?: string;
				};
			};
	  };

const isGiftCodeValidateError = (obj: any): obj is GiftCodeError =>
	obj?.payload && typeof obj.payload.GIFT_CODE_ERROR === 'string';

const hasRedirectUrl = (obj: any): obj is {REDIRECT_URL: string} =>
	typeof obj?.REDIRECT_URL === 'string' && !!obj.REDIRECT_URL;

const isRedeemCodeError = (obj: any): obj is GiftCodeRedeemError =>
	obj?.name === 'USER_NOT_LOGGED' || obj?.name === 'GIFT_CODE_ERROR';

export const useGiftController = () => {
	const t = useTranslation();
	const {status, mutate} = useAutolog();
	const [isLoading, setLoading] = useState(false);
	const [step, setStep] = useState<'code' | 'success'>('code');
	const {getDeezerUrl, getCurrentUrl} = useLocation();
	const router = useRouter();
	const apiCall = useApiCall();
	const toast = useToast();

	const onError = useCallback(
		(errorMessage: string) => {
			toast({
				status: 'error',
				description: errorMessage,
				title: t('error.error'),
			});
			mutate();
			setLoading(false);
		},
		[mutate, toast, t],
	);

	const redeem = useCallback(
		async (code: string) => {
			setLoading(true);
			try {
				await apiCall({
					method: 'premium.checkCodeValidity',
					data: {CODE: code},
				});
			} catch (e) {
				if (isGiftCodeValidateError(e)) {
					onError(e.payload.GIFT_CODE_ERROR);
				} else {
					onError('');
				}
				setLoading(false);
				return;
			}

			try {
				const response = await apiCall({
					method: 'gift.redeemCode',
					data: {CODE: code},
				});

				if (hasRedirectUrl(response)) {
					let redirectUrl = response.REDIRECT_URL;
					if (redirectUrl.startsWith('/')) {
						redirectUrl = `${config.get('deezerWebsiteUrl')}${redirectUrl}`;
					}
					window.location.assign(redirectUrl);
					return;
				}

				mutate();
				setStep('success');
			} catch (e) {
				if (isRedeemCodeError(e)) {
					if (e.name === 'USER_NOT_LOGGED') {
						const registerUrl = new URL(getDeezerUrl('/register'));
						const comebackUrl = new URL(
							getCurrentUrl({withLocale: true, withQuery: true}),
						);
						comebackUrl.searchParams.set('autosubmit', 'true');
						comebackUrl.searchParams.set('code', code);
						registerUrl.searchParams.set('redirect_link', comebackUrl.href);
						location.href = registerUrl.href;
						return;
					} else {
						const newErrorMessage = e.response?.error?.[e.name];
						onError(newErrorMessage ?? '');
					}
				} else {
					onError('');
				}
			} finally {
				setLoading(false);
			}
		},
		[apiCall, getCurrentUrl, getDeezerUrl, mutate, onError],
	);

	const {isReady} = router;
	const {codeParam, autoSubmit} = useMemo(() => {
		const codeParam =
			typeof router.query.code === 'string' ? router.query.code : undefined;
		const autoSubmit = router.query.autosubmit === 'true';
		return {codeParam, autoSubmit};
	}, [router.query]);

	const autoSubmittedRef = useRef(false);

	useEffect(() => {
		if (!isReady || status === AuthStatus.loading) return;
		if (step !== 'code') return;
		if (!autoSubmit || !codeParam) return;
		if (autoSubmittedRef.current) return;

		autoSubmittedRef.current = true;

		const {pathname, query} = router;
		const nextQuery = {...query};
		delete (nextQuery as any).autosubmit;

		(async () => {
			try {
				await redeem(codeParam);
			} finally {
				router.replace({pathname, query: nextQuery}, undefined, {
					shallow: true,
				});
			}
		})();
	}, [isReady, status, step, autoSubmit, codeParam, router, redeem]);

	return {
		isLoading: status === AuthStatus.loading || isLoading,
		redeemCode: redeem,
		step,
		prefilledCode: (router.query.code as string | undefined) ?? undefined,
		isRouterReady: isReady,
	};
};
